import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeAnalytics.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { RefreshCw, AlertCircle, Eye, Users } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeKpiCard from '../components/MicrositeKpiCard/MicrositeKpiCard'
import MicrositeBarChart from '../components/MicrositeBarChart/MicrositeBarChart'
import MicrositeTopPagesTable from '../components/MicrositeTopPagesTable/MicrositeTopPagesTable'
import MicrositeDeviceBreakdown from '../components/MicrositeDeviceBreakdown/MicrositeDeviceBreakdown'
import MicrositeSourceBreakdown from '../components/MicrositeSourceBreakdown/MicrositeSourceBreakdown'

const RANGE_OPTIONS = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' }
]

const formatDate = (d) => d.toISOString().slice(0, 10)

const getDateRange = (daysAgo) => {
    const toDate = new Date()
    const fromDate = new Date()
    fromDate.setDate(fromDate.getDate() - Number(daysAgo))
    return { fromDate: formatDate(fromDate), toDate: formatDate(toDate) }
}

/**
 * MicrositeAnalytics
 * Read-only analytics dashboard. There is no single "summary"
 * endpoint in the API contract — micrositeGetPageViewStats is
 * called once per groupBy value (page, date, device, source) and
 * each result feeds the matching chart/table component. Per-call
 * totals (totalViews, uniqueVisitors) are identical across all 4
 * calls since they're computed over the same date range regardless
 * of groupBy, so the KPI cards use the totals from the first call.
 */
const MicrositeAnalytics = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [rangeDays, setRangeDays] = useState('30')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const [totals, setTotals] = useState({ totalViews: 0, uniqueVisitors: 0 })
    const [byDate, setByDate] = useState([])
    const [byPage, setByPage] = useState([])
    const [byDevice, setByDevice] = useState([])
    const [bySource, setBySource] = useState([])

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadAnalytics = useCallback(async (daysAgo) => {
        setLoading(true)
        setError(false)

        const { fromDate, toDate } = getDateRange(daysAgo)

        const [pageRes, dateRes, deviceRes, sourceRes] = await Promise.all([
            post('micrositeGetPageViewStats', { fromDate, toDate, groupBy: 'page' }),
            post('micrositeGetPageViewStats', { fromDate, toDate, groupBy: 'date' }),
            post('micrositeGetPageViewStats', { fromDate, toDate, groupBy: 'device' }),
            post('micrositeGetPageViewStats', { fromDate, toDate, groupBy: 'source' })
        ])

        if (pageRes?.status === 'success') {
            setTotals({
                totalViews: pageRes.response?.totalViews ?? 0,
                uniqueVisitors: pageRes.response?.uniqueVisitors ?? 0
            })
            setByPage(pageRes.response?.data ?? [])
        } else {
            setError(true)
        }

        if (dateRes?.status === 'success') setByDate(dateRes.response?.data ?? [])
        if (deviceRes?.status === 'success') setByDevice(deviceRes.response?.data ?? [])
        if (sourceRes?.status === 'success') setBySource(sourceRes.response?.data ?? [])

        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadAnalytics(rangeDays)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, rangeDays])

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading analytics…</span>
            </div>
        )
    }

    if (error) {
        return (
            <div className={styles.errorWrap}>
                <AlertCircle size={18} />
                <span>Could not load analytics. Please refresh.</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <MicrositePageHeader
                title="Analytics"
                subtitle="How visitors are finding and using your microsite."
            />

            <div className={styles.rangeRow}>
                <SelectInput value={rangeDays} onChange={setRangeDays} options={RANGE_OPTIONS} />
            </div>

            <div className={styles.kpiGrid}>
                <MicrositeKpiCard icon={Eye} label="Page views" value={totals.totalViews} />
                <MicrositeKpiCard icon={Users} label="Unique visitors" value={totals.uniqueVisitors} />
            </div>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Traffic over time</h2>
                <MicrositeBarChart data={byDate} xKey="groupKey" yKey="totalViews" />
            </section>

            <div className={styles.twoCol}>
                <section className={styles.fieldGroup}>
                    <h2 className={styles.groupTitle}>Top pages</h2>
                    <MicrositeTopPagesTable rows={byPage} />
                </section>

                <section className={styles.fieldGroup}>
                    <h2 className={styles.groupTitle}>Devices</h2>
                    <MicrositeDeviceBreakdown data={byDevice} />
                </section>
            </div>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Traffic sources</h2>
                <MicrositeSourceBreakdown data={bySource} />
            </section>
        </div>
    )
}

export default MicrositeAnalytics